import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  useDeleteTeamMutation,
  useGetTeamQuery,
  useGetDepartmentQuery,
  useGetPositionQuery,
  useGetUserInfoOnyQuery,
} from "../../../Api/attoDeskApi";
import {
  ITeam,
  IDepartment,
  IPosition,
  IUserAdmin,
} from "../../../Api/Interface/api.interface";
import NewPopUpTeam from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import * as XLSX from "xlsx";
import TableViewIcon from "@mui/icons-material/TableView";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs, { Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { useSelector } from "react-redux";
import { RootState } from "../../../Store";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupIcon from "@mui/icons-material/Group";

const capitalizeFirstLetter = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isMobileRes = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10000);
  const [selectedCoursing, setSelectedCoursing] = useState<ITeam | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<ITeam | null>(null);
  const { companyLogo, logowidthPrint, companyName } = useSelector(
    (state: RootState) => state.company
  );

  const [open, setOpen] = useState(false);
  const [selectedEmpImg, setSelectedEmpImg] = useState<string>("");

  const { data, isLoading, isError, refetch } = useGetTeamQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery({
      searchText: "",
    });

  const { data: positionData, isLoading: positionLoading } =
    useGetPositionQuery({
      searchText: "",
    });

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const departmentList = useMemo(() => {
    return departmentData?.data as IDepartment[];
  }, [departmentData?.data]);

  const positionList = useMemo(() => {
    return positionData?.data as IPosition[];
  }, [positionData?.data]);

  const departmentMap = useMemo(() => {
    const map = new Map();
    departmentList?.forEach((category) => {
      map.set(category.id, category.departmentName);
    });
    return map;
  }, [departmentList]);

  const postionMap = useMemo(() => {
    const map = new Map();
    positionList?.forEach((category) => {
      map.set(category.id, category.positionName);
    });
    return map;
  }, [positionList]);

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((category) => {
      map.set(category.id, `${category.user_role_name}`);
    });
    return map;
  }, [userList]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (coursing: ITeam) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: ITeam) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const handleOpen = (imgUrl: string) => {
    setSelectedEmpImg(imgUrl);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedEmpImg("");
    setOpen(false);
  };

  const [deleteCategory] = useDeleteTeamMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the customer");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the customer");
    }
  };

  if (isLoading || userIsLoading || positionLoading || departmentLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  const coursings: ITeam[] = Array.isArray(data?.data)
    ? (data?.data as ITeam[])
    : [];

  const handleAddProductClick = () => {
    navigate("/team/create");
  };

  const handleExportToExcel = () => {
    const exportData = coursings.map((row, index) => ({
      "#": index + 1,
      "Employee Name":
        capitalizeFirstLetter(row.teamFirstName || "") +
          " " +
          capitalizeFirstLetter(row.teamLastName || "") || "",
      "Mobile Number": formatPhoneNumberForDisplay(row.teamMobileNum) || "",
      Position: postionMap.get(row.teamPositionId) || "",
      Deparment: departmentMap.get(row.teamDepartmentId) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StaffDetails");
    const currentMonth = dayjs().format("YYYY MMM, DD");

    const fileName = `StafList_${currentMonth}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const handleExportToPDF = () => {
    const tableData = coursings.map((row, index) => [
      index + 1,
      capitalizeFirstLetter(row.teamFirstName || "") +
        " " +
        capitalizeFirstLetter(row.teamLastName || "") || "",
      formatPhoneNumberForDisplay(row.teamMobileNum) || "",
      postionMap.get(row.teamPositionId) || "",
      departmentMap.get(row.teamDepartmentId) || "",
    ]);

    const tableHeaders = [
      ["#", "Employee Name", "Mobile Number", "Position", "Deparment"],
    ];

    const currentMonth = dayjs().format("YYYY MMM, DD");

    const doc = new jsPDF("l", "mm", "a4");

    const imageUrl = companyLogo;
    const pageWidth = doc.internal.pageSize.width;
    const imgWidth = logowidthPrint;
    const imgHeight = 10;
    // const imgX = (pageWidth - imgWidth) / 2;
    const imgY = 10;
    const imgX = 15;

    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      doc.addImage(img, "PNG", imgX, imgY, imgWidth, imgHeight);

      const textStartY = imgY + imgHeight / 2;

      const centerText = (text: string, y: number) => {
        const textWidth = doc.getTextWidth(text);
        const textX = (pageWidth - textWidth) / 2;
        doc.text(text, textX, y);
      };

      const rightAlignText = (text: string, y: number) => {
        doc.setFontSize(10);
        const textWidth = doc.getTextWidth(text);
        const textX = pageWidth - textWidth - 15;
        doc.text(text, textX, y);
      };

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      rightAlignText(`Staf List`, textStartY);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      rightAlignText(`${currentMonth}`, textStartY + 5);

      (doc as any).autoTable({
        head: tableHeaders,
        body: tableData,
        startY: textStartY + 15,
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [200, 200, 200] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      const fileName = `StafList_${currentMonth}.pdf`;

      doc.save(fileName);
    };
  };

  return (
    <>
      {isMobileRes ? (
        <Card className={classes.card}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={2}
            sx={{ padding: 1 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "flex-start",
                    md: "flex-end",
                  },
                }}
              >
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch className={classes.searchWidgetIcon} />
                        </InputAdornment>
                      ),
                      classes: {
                        root: classes.formControlList,
                      },
                    }}
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Mobile & Name .."
                    size="small"
                    className={classes.searchTextField}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "flex-end",
                  },
                }}
              >
                <Grid item>
                  <Button
                    startIcon={<TableViewIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToExcel}
                  >
                    Excel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<LocalPrintshopSharpIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToPDF}
                  >
                    pdf
                  </Button>
                </Grid>

                <Grid item>
                  <Link
                    to={`/team/create`}
                    className={classes.iconLinkPropNormal}
                  >
                    <Button
                      // variant="contained"
                      className={classes.addNewButton}
                      onClick={handleAddProductClick}
                    >
                      Add New
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheader}>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell
                      className={classes.tablheader}
                      sx={{ textAlign: "right" }}
                    >
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isError || coursings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={20} align="center">
                        {isError ? "Error fetching data" : "No data available"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    coursings
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row: ITeam, index) => (
                        <TableRow
                          key={row.id}
                          className={classes.tableRowHover}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            className={classes.tablCell}
                          >
                            <Grid container spacing={1}>
                              <Grid
                                item
                                textAlign={"left"}
                                alignContent={"center"}
                              >
                                <Link
                                  to={`/team-history/${row.id}`}
                                  className={classes.iconLinkPropNormal}
                                >
                                  <Typography className={classes.nameTableList}>
                                    {capitalizeFirstLetter(
                                      row.teamFirstName || ""
                                    ) +
                                      " " +
                                      capitalizeFirstLetter(
                                        row.teamLastName || ""
                                      )}
                                  </Typography>
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {row.teamEmail}
                                  </Typography>
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {formatPhoneNumberForDisplay(
                                      row.teamMobileNum
                                    )}
                                  </Typography>
                                </Link>
                              </Grid>
                            </Grid>
                          </TableCell>
                          <TableCell scope="row" className={classes.tablCell}>
                            <Grid
                              container
                              spacing={1}
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Grid item>
                                <Link
                                  to={`/team-history/${row.id}`}
                                  className={classes.iconLinkPropNormal}
                                >
                                  <Button
                                    // variant="contained"
                                    className={classes.viewButtonList}
                                    onClick={() =>
                                      navigate(`/team-history/${row.id}`)
                                    }
                                  >
                                    <VisibilitySharpIcon
                                      className={classes.viewListIcon}
                                    />
                                  </Button>
                                </Link>
                              </Grid>
                              <Grid item>
                                <Button
                                  // variant="contained"
                                  className={classes.editButtonList}
                                  onClick={() => handleOpenDialog(row)}
                                >
                                  <EditSharpIcon
                                    className={classes.editListIcon}
                                  />
                                </Button>
                              </Grid>
                              <Grid item>
                                <Button
                                  // variant="contained"
                                  className={classes.deleteButtonList}
                                  onClick={() => handleOpenDeletePopup(row)}
                                >
                                  <DeleteOutlineSharpIcon
                                    className={classes.deleteListIcon}
                                  />
                                </Button>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
              {/* <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid> */}
            </TableContainer>
            {selectedCoursing && (
              <NewPopUpTeam
                openModel={openDialog}
                handleCloseDialog={handleCloseDialog}
                data={selectedCoursing}
              />
            )}
            {coursingToDelete && (
              <DeletePopup
                open={openDeleteCategory}
                handleCloseDelete={handleCloseDeletePopup}
                onConfirm={async () => {
                  await handleDelete(coursingToDelete.id.toString());
                }}
                title="Delete Staff"
                content={`Are you sure you want to delete "${
                  coursingToDelete.teamFirstName +
                  " " +
                  coursingToDelete.teamLastName
                }"?`}
              />
            )}
          </Grid>
        </Card>
      ) : (
        <Card className={classes.card}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "flex-start",
                    md: "flex-end",
                  },
                }}
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch className={classes.searchWidgetIcon} />
                        </InputAdornment>
                      ),
                      classes: {
                        root: classes.formControlList,
                      },
                    }}
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Mobile & Name .."
                    size="small"
                    className={classes.searchTextField}
                  />
                </Grid>

                <Grid item>
                  <Button
                    startIcon={<TableViewIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToExcel}
                  >
                    Excel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<LocalPrintshopSharpIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToPDF}
                  >
                    pdf
                  </Button>
                </Grid>

                <Grid item>
                  <Link
                    to={`/team/create`}
                    className={classes.iconLinkPropNormal}
                  >
                    <Button
                      // variant="contained"
                      className={classes.addNewButton}
                      onClick={handleAddProductClick}
                    >
                      Add New
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheaderId}>
                      <strong>#</strong>
                    </TableCell>
                    <TableCell className={classes.tablheader}>
                      <strong>Name</strong>
                    </TableCell>
                    {!isMobileRes && (
                      <TableCell className={classes.tablheader}>
                        <strong>Position</strong>
                      </TableCell>
                    )}
                    {!isMobileRes && (
                      <TableCell
                        className={classes.tablheader}
                        // sx={{ textAlign: "center" }}
                      >
                        <strong>Create By</strong>
                      </TableCell>
                    )}

                    <TableCell
                      className={classes.tablheader}
                      sx={{ textAlign: "right" }}
                    >
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isError || coursings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={20} align="center">
                        {isError ? "Error fetching data" : "No data available"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    coursings
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row: ITeam, index) => (
                        <TableRow
                          key={row.id}
                          className={classes.tableRowHover}
                        >
                          <TableCell scope="row" className={classes.tablCellId}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            className={classes.tablCell}
                          >
                            {" "}
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs="auto">
                                {/* <Box
                                  className={
                                    classes.SelectImageTableBoxBorder23
                                  }
                                  sx={{ cursor: "pointer" }}
                                > */}
                                {row.teamImg ? (
                                  <Avatar
                                    alt="Img"
                                    src={row.teamImg}
                                    className={classes.avatatImage}
                                    onClick={() => handleOpen(row.teamImg)}
                                    sx={{
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <Avatar className={classes.avatatImage}>
                                    {row.teamFirstName || row.teamLastName ? (
                                      `${row.teamFirstName ?? ""} ${
                                        row.teamLastName ?? ""
                                      }`
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase()
                                    ) : (
                                      <GroupIcon />
                                    )}
                                  </Avatar>
                                )}
                                {/* </Box> */}
                              </Grid>
                              <Grid item xs>
                                <Link
                                  to={`/team-history/${row.id}`}
                                  className={classes.iconLinkPropNormal}
                                >
                                  <Typography className={classes.nameTableList}>
                                    {capitalizeFirstLetter(
                                      row.teamFirstName || ""
                                    ) +
                                      " " +
                                      capitalizeFirstLetter(
                                        row.teamLastName || ""
                                      )}
                                  </Typography>
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {row.teamEmail}
                                  </Typography>
                                  <Typography
                                    className={classes.mobileListText}
                                  >
                                    {formatPhoneNumberForDisplay(
                                      row.teamMobileNum
                                    )}
                                  </Typography>
                                </Link>
                              </Grid>
                            </Grid>
                          </TableCell>
                          {!isMobileRes && (
                            <TableCell
                              scope="row"
                              className={classes.tablCell}
                              // sx={{ textAlign: "center" }}
                            >
                              {postionMap.get(row.teamPositionId) || ""}
                              {/* {departmentMap.get(row.teamDepartmentId) || ""} */}
                            </TableCell>
                          )}
                          {!isMobileRes && (
                            <TableCell
                              scope="row"
                              className={classes.tablCell}
                              // sx={{ textAlign: "center" }}
                            >
                              <Typography>
                                {userMap.get(row.created_by) || ""}
                              </Typography>
                              <Typography>
                                {dayjs(row.created_at).format(
                                  "MMM D, YYYY hh:mm A"
                                )}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell scope="row" className={classes.tablCell}>
                            <Grid
                              container
                              spacing={1}
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Grid item>
                                <Link
                                  to={`/team-history/${row.id}`}
                                  className={classes.iconLinkPropNormal}
                                >
                                  <Button
                                    // variant="contained"
                                    className={classes.viewButtonList}
                                    onClick={() =>
                                      navigate(`/team-history/${row.id}`)
                                    }
                                  >
                                    <VisibilitySharpIcon
                                      className={classes.viewListIcon}
                                    />
                                  </Button>
                                </Link>
                              </Grid>
                              <Grid item>
                                <Button
                                  // variant="contained"
                                  className={classes.editButtonList}
                                  onClick={() => handleOpenDialog(row)}
                                >
                                  <EditSharpIcon
                                    className={classes.editListIcon}
                                  />
                                </Button>
                              </Grid>
                              <Grid item>
                                <Button
                                  // variant="contained"
                                  className={classes.deleteButtonList}
                                  onClick={() => handleOpenDeletePopup(row)}
                                >
                                  <DeleteOutlineSharpIcon
                                    className={classes.deleteListIcon}
                                  />
                                </Button>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
              {/* <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid> */}
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="xs">
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                p={1}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Box>
              <DialogContent sx={{ textAlign: "center" }}>
                {selectedEmpImg ? (
                  <>
                    <img
                      src={selectedEmpImg}
                      alt="Img"
                      style={{
                        width: 200,
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />

                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        component="a"
                        href={selectedEmpImg}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.addButton}
                        startIcon={<DownloadOutlinedIcon />}
                      >
                        Download
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography>No image available</Typography>
                )}
              </DialogContent>
            </Dialog>
            {selectedCoursing && (
              <NewPopUpTeam
                openModel={openDialog}
                handleCloseDialog={handleCloseDialog}
                data={selectedCoursing}
              />
            )}
            {coursingToDelete && (
              <DeletePopup
                open={openDeleteCategory}
                handleCloseDelete={handleCloseDeletePopup}
                onConfirm={async () => {
                  await handleDelete(coursingToDelete.id.toString());
                }}
                title="Delete Staff"
                content={`Are you sure you want to delete "${
                  coursingToDelete.teamFirstName +
                  " " +
                  coursingToDelete.teamLastName
                }"?`}
              />
            )}
          </Grid>
        </Card>
      )}
    </>
  );
};

export default Index;
